import { useState } from 'react'
import { AlertTriangle, ChevronDown, Sparkles } from 'lucide-react'
import { cn } from '../lib/cn'
import { Markdown } from '../Markdown'
import { ScopeBadge } from './ScopeBadge'
import { SuggestionActions, type SuggestionAction } from './SuggestionActions'

export interface UiCitation {
  title?: string
  source?: string
  snippet?: string
}

export interface UiMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  reasoning?: string
  citations?: UiCitation[]
  streaming?: boolean
  /** When set, `content` is an error notice and is rendered as a warning. */
  error?: boolean
  /** Shown above a user bubble, e.g. "Auswahl · 1 Absatz". */
  scopeBadge?: string
}

export interface MessageListProps {
  messages: UiMessage[]
  /** Enables the Ersetzen/Einfügen/Kopieren row under finished answers. */
  onAction?: (message: UiMessage, action: SuggestionAction) => void
  canEdit?: boolean
}

export function MessageList({ messages, onAction, canEdit = true }: MessageListProps) {
  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
      {messages.map((message) =>
        message.role === 'user' ? (
          <UserBubble key={message.id} message={message} />
        ) : (
          <AssistantMessage
            key={message.id}
            message={message}
            onAction={onAction}
            canEdit={canEdit}
          />
        ),
      )}
    </div>
  )
}

function UserBubble({ message }: { message: UiMessage }) {
  return (
    <div className="flex flex-col items-end gap-1.5">
      {message.scopeBadge && <ScopeBadge label={message.scopeBadge} />}
      <div className="max-w-[85%] rounded-2xl bg-neutral-100 px-4 py-2.5 text-sm text-neutral-800">
        {message.content}
      </div>
    </div>
  )
}

function AssistantMessage({
  message,
  onAction,
  canEdit,
}: {
  message: UiMessage
  onAction?: (message: UiMessage, action: SuggestionAction) => void
  canEdit: boolean
}) {
  const done = !message.streaming && message.content.length > 0 && !message.error
  return (
    <div className="space-y-2.5">
      {message.reasoning && <ReasoningPanel text={message.reasoning} />}
      {message.content ? (
        message.error ? (
          <ErrorNotice text={message.content} />
        ) : (
          <Markdown>{message.content}</Markdown>
        )
      ) : message.streaming ? (
        <TypingDots />
      ) : null}
      {message.citations && message.citations.length > 0 && (
        <SourcesList citations={message.citations} />
      )}
      {done && onAction && (
        <SuggestionActions canEdit={canEdit} onAction={(action) => onAction(message, action)} />
      )}
    </div>
  )
}

function ReasoningPanel({ text }: { text: string }) {
  const [open, setOpen] = useState(true)
  return (
    <div className="overflow-hidden rounded-xl border border-reasoning/20 bg-reasoning-soft">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm font-semibold text-reasoning"
      >
        <Sparkles className="h-4 w-4" />
        Nachgedacht
        <ChevronDown className={cn('ml-auto h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="whitespace-pre-wrap px-3 pb-3 text-sm leading-relaxed text-reasoning/90">
          {text}
        </div>
      )}
    </div>
  )
}

function SourcesList({ citations }: { citations: UiCitation[] }) {
  return (
    <div className="space-y-1.5 pt-1">
      <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Quellen</div>
      {citations.map((citation, index) => (
        <div
          key={index}
          className="flex items-center gap-2 rounded-lg border border-neutral-200 px-2.5 py-2 text-sm"
        >
          <span className="flex h-5 w-5 items-center justify-center rounded bg-neutral-100 text-[11px] font-semibold text-neutral-500">
            {index + 1}
          </span>
          <span className="font-medium text-neutral-700">{citation.title ?? citation.source}</span>
          {citation.snippet && (
            <span className="ml-auto truncate text-xs text-neutral-400">{citation.snippet}</span>
          )}
        </div>
      ))}
    </div>
  )
}

function ErrorNotice({ text }: { text: string }) {
  return (
    <div
      role="alert"
      className="flex gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="whitespace-pre-wrap">{text}</span>
    </div>
  )
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1.5" aria-label="Antwortet …">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="h-2 w-2 animate-bounce rounded-full bg-neutral-300"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  )
}
